/** Normalize finance partner / product keys for RetrieveContract row matching. */
export function normPartnerKey(s: string | null | undefined): string {
    return String(s || '')
        .replace(/\s/g, '')
        .toLowerCase();
}

export function contractRowPartner(c: any): string {
    return String(c?.FinancePartner ?? c?.financePartner ?? '');
}

export function contractRowProduct(c: any): string {
    return String(c?.ProductType ?? c?.productType ?? '');
}

/**
 * Trinity `quote-vesrion.component.ts` `buildQuoteVersionsLayout` — long `ProductType` labels → SVA codes.
 * Use before `contract-list.setProductTypes` (RO/R + `RR_Price` → RRR) when `RetrieveContract` sends names
 * like `Solar` / `Solar, Roof` instead of `S` / `SR`.
 */
export function mapQuoteProductTypeLongNameToCode(raw: string): string {
    const t = String(raw ?? '').trim();
    if (!t) return '';
    const key = t
        .replace(/\s*,\s*/g, ',')
        .replace(/\s+/g, '')
        .toLowerCase();
    switch (key) {
        case 'solar':
            return 'S';
        case 'battery':
            return 'B';
        case 'batteryonly':
            return 'B';
        case 'roofonly':
            return 'R';
        case 'solar,battery':
            return 'SB';
        case 'solar,battery,roof':
            return 'SBR';
        case 'solar,roof':
            return 'SR';
        case 'battery,roof':
            return 'BR';
        case 'rr':
            return 'R';
        default:
            return t;
    }
}

export function contractRowQuoteId(c: any): string {
    return String(c?.QuoteId ?? c?.quoteId ?? '');
}

export function contractRowContractId(c: any): string | undefined {
    const v = c?.ContractId ?? c?.Opportunity_Contract_Id ?? c?.opportunityContractId;
    return v != null && String(v).trim() !== '' ? String(v) : undefined;
}

export function contractRowSystemProjectId(c: any): string | undefined {
    const v = c?.SystemProjectId ?? c?.systemProjectId;
    return v != null && String(v).trim() !== '' ? String(v) : undefined;
}

export function contractStatusLower(c: any): string {
    return String(
        c?.Opportunity_Contract_Status ?? c?.contractStatus ?? c?.TrinityContractStatus ?? '',
    ).toLowerCase();
}

/**
 * Pick a prior contract row for change-order flows (same partner + product).
 * Prefers rows that look like an active in-flight contract, excluding cancelled.
 */
export function findPriorContractRowForChangeOrder(
    rawRows: any[],
    partnerDisplay: string,
    productType: string,
): any | null {
    if (!Array.isArray(rawRows) || rawRows.length === 0) return null;
    const np = normPartnerKey(partnerDisplay);
    const pt = String(productType || '').trim();
    const sameLine = rawRows.filter((r) => {
        const fp = normPartnerKey(contractRowPartner(r));
        const rowPt = String(contractRowProduct(r)).trim();
        const fpMatch =
            fp === np ||
            (np.includes('good') && np.includes('leap') && fp.includes('good') && fp.includes('leap')) ||
            (np.includes('lightreach') && fp.includes('lightreach'));
        return fpMatch && rowPt === pt;
    });
    if (sameLine.length === 0) return null;
    const activeFirst = sameLine.find((r) => {
        const st = contractStatusLower(r);
        return st && !st.includes('cancel');
    });
    return activeFirst ?? sameLine[0];
}

export function firstSigningLikeUrl(data: unknown): string | null {
    if (data == null) return null;
    if (typeof data === 'string' && /^https?:\/\//i.test(data.trim())) return data.trim();
    const o = data as Record<string, unknown>;
    /** Trinity `GetRoofingProposalURL` / proposal flows often return `downloadUrl` (see `SunnovaTrinityContractModelComponent`). */
    const candidates = [
        o?.downloadUrl,
        o?.DownloadUrl,
        o?.file,
        o?.File,
        o?.Url,
        o?.url,
        o?.SigningUrl,
        o?.signingUrl,
        o?.Data,
        (o?.Data as Record<string, unknown> | undefined)?.url,
        (o?.Data as Record<string, unknown> | undefined)?.Url,
        (o?.Data as Record<string, unknown> | undefined)?.downloadUrl,
        (o?.Data as Record<string, unknown> | undefined)?.DownloadUrl,
    ];
    for (const c of candidates) {
        if (typeof c === 'string' && /^https?:\/\//i.test(c.trim())) return c.trim();
    }
    return null;
}

/**
 * Proposal URL from `GetRoofingProposalURL` — aligns with
 * `SunnovaTrinityContractModelComponent.getRoofProposalDocumet` (Angular treats success as `res.downloadUrl`).
 * Also unwraps `data`/`Data`, and `url` / `documents` arrays (same shape as `createDocumateSunnovaAPI` / template loops).
 */
export function roofingProposalUrlFromApi(res: unknown): string | null {
    const pick = (x: unknown) => firstSigningLikeUrl(x);
    const direct = pick(res);
    if (direct) return direct;
    if (res == null || typeof res !== 'object') return null;
    const o = res as Record<string, unknown>;
    for (const wrap of [o.data, o.Data, o.result, o.Result]) {
        const u = pick(wrap);
        if (u) return u;
    }
    const urlVal = o.url;
    if (Array.isArray(urlVal)) {
        for (const item of urlVal) {
            const u = pick(item);
            if (u) return u;
        }
    } else if (urlVal != null && typeof urlVal === 'object') {
        const u = pick(urlVal);
        if (u) return u;
    }
    const docs = o.documents ?? o.Documents;
    if (Array.isArray(docs)) {
        for (const item of docs) {
            const u = pick(item);
            if (u) return u;
        }
    }
    return null;
}

/** Trinity `OpportunityConstant.InvalidVendorMessageForGoodLeap`. */
export const INVALID_VENDOR_MESSAGE_FOR_GOOD_LEAP =
    'The selected Module is no longer accepted by Good Leap, please update the module. Reach out to your manager regarding which specific equipment to select or if the system is already installed.';

type GoodLeapValidationError = {
    description?: string;
    code?: string;
    type?: string;
};

function isGoodLeapFinancePartner(financePartner: string | null | undefined): boolean {
    return String(financePartner ?? '').replace(/\s/g, '').toLowerCase() === 'goodleap';
}

function parseGoodLeapErrorContent(content: unknown): {
    parsedError: GoodLeapValidationError | null;
    messageToDisplay: string;
} | null {
    if (content == null) return null;

    if (typeof content === 'string') {
        let messageToDisplay = content;
        let parsedError: GoodLeapValidationError | null = null;
        try {
            const parsed = JSON.parse(content) as unknown;
            parsedError = (Array.isArray(parsed) ? parsed[0] : parsed) as GoodLeapValidationError;
        } catch {
            parsedError = null;
        }
        return { parsedError, messageToDisplay };
    }

    const parsedError = (Array.isArray(content) ? content[0] : content) as GoodLeapValidationError;
    let messageToDisplay: string;
    try {
        messageToDisplay = JSON.stringify(content);
    } catch {
        messageToDisplay = 'Unknown error occurred';
    }
    return { parsedError, messageToDisplay };
}

export function isGoodLeapInvalidVendorError(
    financePartner: string | null | undefined,
    parsedError: GoodLeapValidationError | null | undefined,
): boolean {
    return (
        isGoodLeapFinancePartner(financePartner) &&
        parsedError?.description === 'Invalid vendor' &&
        parsedError?.code === 'INVALID_PARAMETER' &&
        parsedError?.type === 'VALIDATION'
    );
}

/**
 * Trinity `DetailCellRendererComponent.formatErrorMessage` — JSON `Message` → readable text.
 */
export function formatGoodLeapSyncErrorMessage(message: string): string {
    if (!message.trim()) return message;
    try {
        const parsed = JSON.parse(message) as unknown;
        if (Array.isArray(parsed)) {
            return parsed
                .map((item: Record<string, unknown>) =>
                    String(item.description ?? item.message ?? JSON.stringify(item)),
                )
                .join(', ');
        }
        if (parsed && typeof parsed === 'object') {
            const p = parsed as Record<string, unknown>;
            const reasons = Array.isArray(p.reasons) && p.reasons.length ? `Reasons: ${p.reasons.join(', ')}` : '';
            const mainMessage = p.message ? `Message: ${String(p.message)}` : '';
            if (mainMessage && reasons) return `${mainMessage} ${reasons}`.trim();
            return (
                mainMessage ||
                reasons ||
                String(p.error ?? p.description ?? JSON.stringify(parsed))
            );
        }
        return message;
    } catch {
        return message;
    }
}

/**
 * Trinity `DetailCellRendererComponent.showInvalidError` — toast text for Good Leap sync failures.
 */
export function goodLeapSyncErrorNotice(
    errorObj: unknown,
    financePartner: string | null | undefined,
): string | null {
    if (errorObj == null) return null;

    let content: unknown;
    if (typeof errorObj === 'object' && errorObj !== null) {
        const o = errorObj as Record<string, unknown>;
        content = o.Message ?? o.message;
        if (content == null) {
            if (Array.isArray(errorObj) || ('code' in o && 'description' in o)) {
                content = errorObj;
            } else if (o.error && (Array.isArray(o.error) || typeof o.error === 'object')) {
                content = o.error;
            }
        }
    } else {
        content = errorObj;
    }

    const parsed = parseGoodLeapErrorContent(content);
    if (!parsed) return null;

    if (isGoodLeapInvalidVendorError(financePartner, parsed.parsedError)) {
        return INVALID_VENDOR_MESSAGE_FOR_GOOD_LEAP;
    }
    return parsed.messageToDisplay;
}

/**
 * Trinity `showSyncErrorPopup` / `processRemarks` — map Invalid vendor in stored sync remarks.
 */
export function goodLeapSyncRemarkDescription(
    item: GoodLeapValidationError & { error?: string; field?: string },
    financePartner: string | null | undefined,
    label: string,
): string | null {
    let description = item?.description;
    if (normPartnerKey(financePartner).includes('enfin') && item?.error) {
        const field = String(item.field ?? '').replace(/\n/g, ', ');
        description = `${field} - ${String(item.error).replace('Required ', '')}`;
    }
    if (!description) return null;
    const isInvalidVendor =
        (isGoodLeapFinancePartner(financePartner) || normPartnerKey(financePartner).includes('enfin')) &&
        label === 'System Design' &&
        description === 'Invalid vendor' &&
        item?.code === 'INVALID_PARAMETER' &&
        item?.type === 'VALIDATION';
    return isInvalidVendor ? INVALID_VENDOR_MESSAGE_FOR_GOOD_LEAP : description;
}

export type LoanSyncResyncErrors = {
    systemDesign?: string;
    utility?: string;
    documentSent?: string;
};

/** Trinity `getSyncErrorDescription` — whether stored loan-status remarks justify the sync-error affordance. */
export function loanSyncHasStoredErrors(
    loanStatus: Record<string, unknown>,
    financePartner: string | null | undefined,
): boolean {
    if (
        parseLoanSyncRemarkDescriptions(
            'System Design',
            loanStatus.System_Design_Sync_Remarks,
            financePartner,
        ).length
    ) {
        return true;
    }
    if (parseLoanSyncRemarkDescriptions('Utility', loanStatus.Utility_Sync_Remarks, financePartner).length) {
        return true;
    }
    const doc = loanStatus.Document_Sent_Status;
    return typeof doc === 'string' && doc.trim().toLowerCase().includes('failed');
}

/** Trinity `showSyncErrorPopup` `processRemarks`. */
export function parseLoanSyncRemarkDescriptions(
    label: string,
    remarks: unknown,
    financePartner: string | null | undefined,
): string[] {
    if (remarks == null || !String(remarks).trim()) return [];
    try {
        const parsed = JSON.parse(String(remarks)) as unknown;
        const descriptions: string[] = [];
        if (Array.isArray(parsed)) {
            parsed.forEach((item) => {
                const d = goodLeapSyncRemarkDescription(
                    item as GoodLeapValidationError & { error?: string; field?: string },
                    financePartner,
                    label,
                );
                if (d) descriptions.push(d);
            });
        } else {
            const d = goodLeapSyncRemarkDescription(
                parsed as GoodLeapValidationError & { error?: string; field?: string },
                financePartner,
                label,
            );
            if (d) descriptions.push(d);
        }
        return descriptions;
    } catch {
        return [];
    }
}

/**
 * Trinity `formatErrorMessage` for resync popup bullets — supports `{ message, reasons[] }` payloads.
 */
export function formatGoodLeapSyncResyncDisplayLines(message: string): string[] {
    const trimmed = message.trim();
    if (!trimmed) return [];
    try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
            return parsed.map((item: Record<string, unknown>) =>
                String(item.description ?? item.message ?? JSON.stringify(item)),
            );
        }
        if (parsed && typeof parsed === 'object') {
            const p = parsed as Record<string, unknown>;
            const lines: string[] = [];
            if (p.message) lines.push(`Message: ${String(p.message)}`);
            if (Array.isArray(p.reasons) && p.reasons.length) {
                lines.push(`Reasons: ${(p.reasons as unknown[]).map(String).join(', ')}`);
            }
            if (lines.length) return lines;
            return [String(p.error ?? p.description ?? JSON.stringify(parsed))];
        }
    } catch {
        // fall through
    }
    if (trimmed.includes('Message:') && trimmed.includes('Reasons:')) {
        const reasonsIdx = trimmed.indexOf('Reasons:');
        if (reasonsIdx > 0) {
            return [trimmed.slice(0, reasonsIdx).trim(), trimmed.slice(reasonsIdx).trim()].filter(Boolean);
        }
    }
    return [trimmed];
}

export function mapGoodLeapResyncSystemDesignDisplay(
    message: string,
    financePartner: string | null | undefined,
): string[] {
    if (isGoodLeapFinancePartner(financePartner) && message.includes('Invalid vendor')) {
        return [INVALID_VENDOR_MESSAGE_FOR_GOOD_LEAP];
    }
    return formatGoodLeapSyncResyncDisplayLines(message);
}

/** Stored in component state for the “Resync Errors” popup section (Trinity `formatErrorMessage`). */
export function formatGoodLeapResyncErrorStorage(
    stage: 'Utility' | 'System Design' | 'Document Sent',
    payload: unknown,
    financePartner: string | null | undefined,
): string {
    const raw =
        payload != null && typeof payload === 'object'
            ? String((payload as { Message?: string }).Message ?? '').trim()
            : String(payload ?? '').trim();
    if (stage === 'Utility') {
        return formatGoodLeapSyncErrorMessage(raw) || raw || 'Utility sync failed.';
    }
    if (stage === 'System Design') {
        const formatted = formatGoodLeapSyncErrorMessage(raw);
        if (isGoodLeapFinancePartner(financePartner) && (formatted.includes('Invalid vendor') || raw.includes('Invalid vendor'))) {
            return INVALID_VENDOR_MESSAGE_FOR_GOOD_LEAP;
        }
        return formatted || goodLeapSyncErrorNotice(payload, financePartner) || raw || 'Sync failed.';
    }
    return raw || formatGoodLeapSyncErrorMessage(raw) || goodLeapSyncErrorNotice(payload, financePartner) || 'Sync failed.';
}

export function loanSyncHasAnyErrors(
    loanStatus: Record<string, unknown>,
    financePartner: string | null | undefined,
    resyncErrors?: LoanSyncResyncErrors,
): boolean {
    if (loanSyncHasStoredErrors(loanStatus, financePartner)) return true;
    if (!resyncErrors) return false;
    return !!(resyncErrors.systemDesign?.trim() || resyncErrors.utility?.trim() || resyncErrors.documentSent?.trim());
}

/** Message from failed contract HTTP calls (Axios `response.data.Message` or Angular-style `customMessage`). */
export function contractApiErrorMessage(e: unknown): string | undefined {
    if (!e || typeof e !== 'object') return undefined;
    const o = e as Record<string, unknown>;
    const custom = o.customMessage;
    if (typeof custom === 'string' && custom.trim()) return custom.trim();
    const resp = o.response as Record<string, unknown> | undefined;
    const data = resp?.data;
    if (data != null && typeof data === 'object') {
        const d = data as Record<string, unknown>;
        const m = d.Message ?? d.message;
        if (typeof m === 'string' && m.trim()) return m.trim();
    }
    return undefined;
}
